import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Modal } from "../../../components";
import { MARKET_ABI } from "../../../contracts/contracts";
import {
  MARKET_ADDRESS,
  SUBGRAPH_URL,
} from "../../../components/Features/Market/Market";
import { getNFTMetadata } from "../../Features/Market/Market";
import { useWallet } from "../../../contexts/WalletContext";
import { useAuth } from "../../../contexts/AuthContext";
import SEO from "../../../shared/components/SEO";
import {
  organizationSchema,
  buildBreadcrumbSchema,
  buildVisualArtworkSchema,
  buildGraph,
  truncateDescription,
  SITE_URL,
} from "../../../shared/utils/seoSchemas";

import ReactDOM from "react-dom";
import axios from "axios";

const ProductPage = ({ data }) => {
  const { contract_address, token_id } = useParams();
  const location = useLocation();
  const {
    ethAddress: walletAddress,
    connectWallet,
    isConnecting,
  } = useWallet();
  const { ethAddress: authEthAddress } = useAuth();

  const activeAddress = walletAddress || authEthAddress;
  const [item, setItem] = useState(location.state?.item || {});
  const [metadata, setMetadata] = useState(null);

  const [activeTab, setActiveTab] = useState("about");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOnChainOpen, setIsOnChainOpen] = useState(true);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [usdPrice, setUsdPrice] = useState(null);

  useEffect(() => {
    const fetchUsdPrice = async () => {
      if (!item?.price || isNaN(Number(item.price))) {
        return;
      }

      try {
        const response = await axios.get(
          "https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd",
        );

        const ethToUsdRate = response?.data?.ethereum?.usd;

        if (!ethToUsdRate) return;

        const priceInUsd = (Number(item.price) * ethToUsdRate).toFixed(2);

        setUsdPrice(priceInUsd);
      } catch (error) {
        console.error("ETH/USD fetch error:", error);
      }
    };

    fetchUsdPrice();
  }, [item?.price]);


  useEffect(() => {
    const loadMetadata = async () => {
      if (!location.state?.metadataUri) return;

      try {
        const meta = await getNFTMetadata(location.state.metadataUri);

        setMetadata({
          ...meta,
          image: meta?.image?.startsWith("ipfs://")
            ? meta.image.replace("ipfs://", "https://ipfs.io/ipfs/")
            : meta.image,
        });
      } catch (err) {
        console.error("Metadata fetch error:", err);
      }
    };

    loadMetadata();
  }, [location.state]);
  useEffect(() => {
    if (location.state?.item) {
      setItem(location.state.item);
    }
  }, [location.state]);
  const handlePurchaseClick = () => {
    if (!activeAddress) {
      setShowWalletModal(true);
      return;
    }

    setIsModalOpen(true);
  };

  useEffect(() => {
    const fetchItemByParams = async () => {
      if (location.state?.item) return;

      try {
        const query = `
        {
          listings(
            where: {
              collection: "${contract_address.toLowerCase()}"
              tokenId: "${token_id}"
            }
            first: 1
          ) {
            id
            seller
            quantity
            pricePerToken
            tokenId
            token {
              id
              tokenId
              metadataUri
              collection {
                id
                name
                description
                contractURI
              }
            }
          }
        }
      `;

        const response = await fetch(SUBGRAPH_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ query }),
        });

        const result = await response.json();

        const listing = result?.data?.listings?.[0];

        if (!listing) return;

        const meta = await getNFTMetadata(listing.token?.metadataUri);
        if (!meta) return;

        setMetadata(meta);

        const rawMeta = meta?.raw;
        const findAttr = (key) =>
          meta?.attributes?.find(
            (a) => a.trait_type?.toLowerCase() === key.toLowerCase(),
          )?.value;

        setItem({
          ...listing,
          metadataUri: listing.token?.metadataUri,
          nft: listing.token?.collection,
          token: listing.token,
          name: meta?.name || listing.token?.collection?.name,
          description: meta?.description || listing.token?.collection?.description,
          image: meta?.image,
          preview: meta?.preview || meta?.image,
          prev: meta?.preview || meta?.image,
          year: rawMeta?.year || findAttr("Year"),
          size: rawMeta?.size || findAttr("Size"),
          quantity: listing.quantity,
          price: (Number(listing.pricePerToken) / 1e18).toFixed(4),
        });
      } catch (err) {
        console.error(err);
      }
    };

    fetchItemByParams();
  }, [contract_address, token_id, location.state]);

  const displayName = metadata?.name || item.name || "";
  const collectionName =
    item.nft?.name || metadata?.collection || item.collection || "Reproduction Collection";
  const productDescription = truncateDescription(
    metadata?.description ||
      item.nft?.description ||
      item.description ||
      `${displayName || "This item"} is a tokenized reproduction artwork available on Funooni, a digital art and NFT marketplace for Arab and MENA artists.`,
  );
  const canonicalUrl = `${SITE_URL}/reproduction/${contract_address}/${token_id}`;
  const productImage =
    metadata?.preview || metadata?.image || item.image || item.preview || item.prev || "";

  const productSeo = displayName && {
    title: `${displayName} | Funooni Reproduction`,
    description: productDescription,
    canonicalUrl,
    robots: "index, follow",
    ogTitle: `${displayName} | Funooni Reproduction`,
    ogDescription: productDescription,
    ogImage: productImage,
    ogUrl: canonicalUrl,
    ogType: "product",
    twitterCard: "summary_large_image",
    structuredData: buildGraph(
      organizationSchema,
      buildBreadcrumbSchema([
        { name: "Home", url: `${SITE_URL}/` },
        { name: "Reproduction", url: `${SITE_URL}/reproduction` },
        { name: collectionName, url: `${SITE_URL}/reproduction/collections` },
        { name: displayName, url: canonicalUrl },
      ]),
      buildVisualArtworkSchema({
        name: displayName,
        description: productDescription,
        image: productImage,
        url: canonicalUrl,
        artMedium: item.size ? `Reproduction print, ${item.size}` : "Reproduction print",
      }),
    ),
  };

  return (
    <div className="art-single-page">
      {productSeo && <SEO {...productSeo} />}
      <div className="ars-container2">
        <div className="ars-preview-container2">
          <div className="ars-preview2" onClick={() => setIsModalOpen(true)}>
            <img
              src={
                metadata?.preview ||
                metadata?.image ||
                item.image ||
                item.preview ||
                item.prev
              }
              alt={metadata?.name || item.name}
            />
          </div>
        </div>

        <div className="ars-info">
          <div className="ars-title">
            <h1>{metadata?.name || item.name}</h1>
          </div>

          <div className="ars-title-under">
            <span>{item?.price} ETH</span>
            <span> {item.quantity} out of 50 Available</span>
          </div>
          <button className="purchase-btn" onClick={handlePurchaseClick}>
            Purchase
          </button>
        </div>
      </div>

      <div className="tabs">
        <button
          className={activeTab === "about" ? "active" : ""}
          onClick={() => setActiveTab("about")}
        >
          About the work
        </button>
      </div>

      {activeTab === "about" && (
        <div className="about-section">
          <div>
            <div className="info-grid">
              <p className="info-grid-p">Name</p>
              <span>{metadata?.name || item.name}</span>
            </div>

            <div className="info-grid">
              <p className="info-grid-p">Collection</p>
              {item.nft?.name || item.collection || "Unknown Collection2"}{" "}
            </div>
            <div className="info-grid">
              <p className="info-grid-p">Size</p>
              <span>{item.size}</span>
            </div>
            <div className="info-grid">
              <p className="info-grid-p">Price</p>
              <span>
                {item.price} ETH ~ {usdPrice} USD{" "}
              </span>
            </div>

            <div className="info-grid">
              <p className="info-grid-p">Quantity</p>
              <span>{item.quantity}</span>
            </div>
          </div>

          <div className="onchain">
            <div
              className="onchain-header"
              onClick={() => setIsOnChainOpen((prev) => !prev)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                cursor: "pointer",
                alignItems: "center",
              }}
            >
              <h4 style={{ margin: 0 }}>On-Chain Information</h4>

              <span style={{ fontSize: "18px" }}>
                {isOnChainOpen ? "▾" : "▸"}
              </span>
            </div>

            <div
              className={`onchain-content ${isOnChainOpen ? "open" : "closed"}`}
            >
              <div className="product-description contract-grid">
                <h4>Description</h4>
                <p>
                  {metadata?.description ||
                    item.nft?.description ||
                    item.description ||
                    "No description available."}
                </p>
              </div>
              <div className="contract-grid">
                <p className="contract-grid-name">Collection name</p>
                <span>
                  {item.nft?.name || metadata?.collection || item.collection}
                </span>
              </div>

              <div className="contract-grid">
                <strong className="contract-grid-desc">
                  About the Collection
                </strong>
                <span>
                  {item.nft?.description ||
                    item.token?.collection?.description ||
                    location.state?.collectionDescription ||
                    "No description available2"}
                </span>
              </div>

              <div className="contract-grid">
                <strong>Contract Address</strong>
                <span>
                  {item.token?.collection?.id ||
                    item.token?.id ||
                    item.nft?.id ||
                    item.id}
                </span>
              </div>

              <div className="contract-grid">
                <strong>Created by</strong>
                <span>{item?.seller}</span>
              </div>

              <div className="contract-grid">
                <strong>Chain</strong>
                <span>{item.ethereum || "Ethereum"}</span>
              </div>

              {/* <div className="contract-grid">
                <strong>Token Standard</strong>
                <span>ERC-721</span>
              </div> */}
            </div>
            {/* )} */}
          </div>
        </div>
      )}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        artItem={item}
        source_item={metadata?.image || item.preview}
        MARKET_ADDRESS={MARKET_ADDRESS}
        MARKET_ABI={MARKET_ABI}
        useFormattedPrice={false}
      />
      {showWalletModal &&
        ReactDOM.createPortal(
          <div
            className="modal-overlay-global"
            onClick={() => setShowWalletModal(false)}
          >
            <div
              className="modal-box-global"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Connect Your Wallet</h3>
              <p>You need to connect your wallet to continue.</p>

              <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
                <button onClick={connectWallet}>
                  {isConnecting ? "Connecting..." : "Connect Wallet"}
                </button>

                <button onClick={() => setShowWalletModal(false)}>Close</button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ProductPage;
